import { useJumboLayoutSidebar } from '@jumbo/hooks';
import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getDecisionStatusListApi } from 'api/meeting';
import { getUserFullName, isRoleCheckList } from 'app/utils/appHelpers';
import config from 'config';
import { prodYKToplantisiUnitId } from 'config';
import 'dayjs/locale/tr';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// specificOtherDepts dizisi, istenen yapıda her zaman son kısımda alfabetik sıralı olarak eklenecek.
const specificOtherDepts = ["SGDB", "IKDHB", "BÇKDB", "HM", "İç Denetim Birimi"].sort();

/*
  Type "yk" için zorunlu (non-removable) departman değerlerini belirleyen yardımcı fonksiyon:
  Eğer userRoles içinde:
    - 416 id'li rol varsa VE rolün inst değeri "YK" ise
    - veya 370/372 id'li roller varsa ve bunların inst değeri "YK" ise,
  bu durumda yapı olduğu gibi kalacak (departman seçeneklerinde tüm departmanlar kullanılacak).
  Aksi durumda, 
    - eğer 370/372 id'li roller yoksa ve 416 varsa fakat inst "YK" değil
    - veya 373/369 id'li roller varsa,
  ilgili rollerden unique inst değerleri alınarak, departman seçeneklerine eklenir ve
  sonrasında alfabetik sıralı specificOtherDepts dizisi eklenir.
*/
const getRequiredDeptsForYK = (userRoles) => {
  const has416_YK = userRoles.some(r => r.id === 416 && r.inst.toUpperCase() === "YK");
  const has370or372_YK = userRoles.some(r => [370, 372].includes(r.id) && r.inst.toUpperCase() === "YK");

  // Eğer 416 veya 370/372 id'li roller varsa ve inst "YK" ise yapı olduğu gibi çalışsın.
  if (has416_YK || has370or372_YK) {
    return null;
  }


  // Eğer 370/372 rolleri bulunmuyorsa fakat 416 varsa fakat inst "YK" değil
  // veya 373 veya 369 id'li roller varsa:
  const condition = (
    (!userRoles.some(r => [370, 372].includes(r.id)) && userRoles.some(r => r.id === 416 && r.inst.toUpperCase() !== "YK"))
    || userRoles.some(r => [373, 369].includes(r.id))
  );

  if (condition) {
    const insts = userRoles
      .filter(r => ((r.id === 416 && r.inst.toUpperCase() !== "YK") || [373, 369].includes(r.id)))
      .map(r => r.inst);
    const uniqueInsts = Array.from(new Set(insts));

    return [...uniqueInsts, ...specificOtherDepts];
  }
  return null;

};





function DecisionStatusListDataTableFilter({ data, setFilterData, setSelectedFilters, setLoading, type, setSearchType }) {

  const [filters, setFilters] = useState({
    // Decision tablosu filtreleri
    decisionDepartments: [],
    decisionSubjectIds: [],
    // decisionText: "",
    agendaText: "",
    principle: null,
    supp: null,
    secret: null,
    applied: false,      // Uygulananlar için checkbox
    notApplied: false,   // Uygulanmayanlar için checkbox

    // Yeni: meetingNo textInput alanı
    meetingNo: "",

    // Meeting tablosu filtreleri
    meetingAdminUserIds: [],
    meetingInst: [],
    meetingUnitIds: [],
    statFinish: null,
    pdateStart: null,
    pdateEnd: null,

    // MeetingUser tablosu filtreleri
    meetingCreatorUserIds: [],
    meetingParticipantUserIds: [],

    // statusReview tablosu filtreleri
    statusReviewUserIds: [],
    pageType: "dstatus"
  });

  const {
    decisionDepartments,
    decisionSubjectIds,
    agendaText,
    principle,
    supp,
    secret,
    applied,
    notApplied,
    meetingNo,
    meetingAdminUserIds,
    meetingInst,
    meetingUnitIds,
    statFinish,
    pdateStart,
    pdateEnd,
    meetingCreatorUserIds,
    meetingParticipantUserIds,
    statusReviewUserIds,
    pageType
  } = filters;

  const { authUser } = useSelector((state) => state.auth);
  const [dataUnitOptions, setDataUnitOptions] = useState([]);
  const [userListOptions, setUserListOptions] = useState([]);
  const [dataSubjectOptions, setDataSubjectOptions] = useState([]);
  const [dataInstOptions, setDataInstOptions] = useState([]);
  const [dataDepartmentOptions, setDataDepartmentOptions] = useState([]);
  // fixedDepartment: zorunlu silinemez departmanlar (varsa array olarak saklanacak)
  const [fixedDepartment, setFixedDepartment] = useState(null);
  // type !== "yk" için meetingInst'de zorunlu kalan değerleri saklayan state
  const [fixedInst, setFixedInst] = useState(null);
  const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();

  const unitList = useSelector((state) => state.unit.data || []);
  const userList = useSelector((state) => state.user.data || []);
  const subjectList = useSelector((state) => state.subject.data || []);

  const allAuthorizedUnits = useSelector(state => state.auth.allAuthorizedUnits);
  const allAuthorizedInsts = useSelector(state => state.auth.allAuthorizedInsts);
  const [anotherYkUnit, setAnotherYkUnit] = useState(false);


  useEffect(() => {
    setUserListOptions(userList);
    setDataSubjectOptions(subjectList);
    // --- Departments ayarı ---
    if (type === "yk") {


      const requiredDeps = getRequiredDeptsForYK(authUser.userRoles);
      if (requiredDeps) {

        let updatedDepartments;
        const nonSpecific = requiredDeps.filter(dep => !specificOtherDepts.includes(dep));
        if (nonSpecific.length === 1) {
          updatedDepartments = config.departments
            .map(item => item.name)
            .filter(d => d === nonSpecific[0] || specificOtherDepts.includes(d));
        } else {
          updatedDepartments = config.departments.map(item => item.name);
        }
        setDataDepartmentOptions(updatedDepartments);
        setFixedDepartment(nonSpecific); // kullanıcının silememesi gereken zorunlu departmanlar
      } else {
        // Eğer 416 (veya 370/372) id'li rol varsa ve inst değeri "YK" ise; tüm departmanlar kullanılsın.
        setDataDepartmentOptions(config.departments.map(item => item.name));
        setFixedDepartment(null);
      }
    } else {
      // type !== "yk" durumunda departmanlar olduğu gibi kalsın.
      setDataDepartmentOptions(config.departments.map(item => item.name));
      setFixedDepartment(null);
    }

    // --- Units ve Inst ayarı ---
    if (type === "yk") {

      const customId372 = authUser.userRoles.filter(r => [372].includes(r.id)).map(r => r.customId);
      if (isRoleCheckList(authUser.userRoles, [370, 1])) {
        const ykUnit = unitList.filter(unit => unit.inst === "YK");
        setDataUnitOptions([ykUnit]);
        setFilters(prev => ({
          ...prev,
          meetingInst: ["YK"],
          meetingUnitIds: [ykUnit]
        }));
        setAnotherYkUnit(true);
        setDataUnitOptions([...ykUnit]);
      } else if (customId372.length > 0 && !customId372.includes(Number(prodYKToplantisiUnitId))) {
        const ykUnit1 = unitList.filter(unit => customId372.includes(unit.id));
        const ykUnit2 = unitList.find(unit => unit.id === prodYKToplantisiUnitId);

        // Combine ykUnit1 and ykUnit2 into a single array
        const combinedUnits = [...ykUnit1, ...(ykUnit2 ? [ykUnit2] : [])];
        setDataUnitOptions(combinedUnits);

        setFilters(prev => ({
          ...prev,
          meetingInst: ["YK"],
          meetingUnitIds: combinedUnits // Use the combined array for meetingUnitIds
        }));
        setAnotherYkUnit(true);
      } else {
        const ykUnit = unitList.find(unit => unit.id === prodYKToplantisiUnitId);
        setDataUnitOptions([ykUnit]);
        setFilters(prev => ({
          ...prev,
          meetingInst: ["YK"],
          meetingUnitIds: [ykUnit]
        }));
        setAnotherYkUnit(false);
      }
    } else if (type === "kurul") {
      setAnotherYkUnit(false);
      if (isRoleCheckList(authUser.userRoles, [1])) {
        const nonYkUnits = unitList.filter(unit => unit.inst !== "YK");
        setDataUnitOptions(nonYkUnits);
        const nonYkInsts = [...new Set(unitList.map(unit => unit.inst).filter(inst => inst !== ''))];
        setDataInstOptions(nonYkInsts);

      }
      if (isRoleCheckList(authUser.userRoles, [373])) {

        setDataInstOptions(allAuthorizedInsts);
        setFixedInst(allAuthorizedInsts); // fixedInst: sabit olarak (ör: ["ATAM", "TDK"]) belirlendi.

        setDataUnitOptions(allAuthorizedUnits);
      }
      else if (isRoleCheckList(authUser.userRoles, [417, 369])) {
        // Sadece 417 ve 369 id'li rollerin inst değerlerini alıyoruz
        const insts = authUser.userRoles.filter(r => [417, 369].includes(r.id)).map(r => r.inst);
        const uniqueInsts = Array.from(new Set(insts));
        setDataInstOptions(uniqueInsts);
        setFixedInst(uniqueInsts); // fixedInst: sabit olarak (ör: ["ATAM", "TDK"]) belirlendi.

        const userUnits = unitList.filter(unit => uniqueInsts.includes(unit.inst));
        setDataUnitOptions(userUnits);
      }
      else {
        // Yönetici değilse
        const currentInst = dataInstOptions.length > 0 ? dataInstOptions[0] : '';
        setDataInstOptions(currentInst ? [currentInst] : []);
        const userUnits = unitList.filter(unit => unit.inst === currentInst);
        setDataUnitOptions(userUnits);
      }

    }
  }, [type]);

  useEffect(() => {
    if (type === "yk") {
      setFilters(prev => ({
        ...prev,
        meetingInst: ["YK"]
      }));
      if (fixedDepartment && Array.isArray(fixedDepartment) && fixedDepartment.length > 0) {
        setFilters(prev => ({
          ...prev,
          decisionDepartments: fixedDepartment
        }));
      }
    } else {
      if (isRoleCheckList(authUser.userRoles, [1])) {
        // Yönetici rolü (1) var ise; type !== "yk" durumunda, varsayılan olarak "YK" hariç tüm instler seçili gelsin.
        const defaultInsts = dataInstOptions.filter(inst => inst.toUpperCase() !== "YK");
        setFilters(prev => ({
          ...prev,
          meetingInst: defaultInsts
        }));
      } else if (isRoleCheckList(authUser.userRoles, [417, 373, 369])) {
        const insts = authUser.userRoles.filter(r => [417, 373, 369].includes(r.id)).map(r => r.inst);
        const uniqueInsts = Array.from(new Set(insts));
        setFilters(prev => ({
          ...prev,
          meetingInst: uniqueInsts
        }));
      } else {
        const currentInst = dataInstOptions.length > 0 ? dataInstOptions[0] : '';
        setFilters(prev => ({
          ...prev,
          meetingInst: currentInst ? [currentInst] : []
        }));
      }
    }
  }, [type, dataInstOptions, fixedDepartment, authUser]);

  useEffect(() => {
    setFilterData(data);
  }, [data]);

  useEffect(() => {
    handleClear();
    setExpanded(false);
  }, [type]);

  const handleInputChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleDateChange = (field) => (newValue) => {
    setFilters((prev) => ({ ...prev, [field]: newValue }));
  };

  useEffect(() => {
    if (type === "yk" && fixedDepartment && fixedDepartment.length > 0) {
      setFilters(prev => ({ ...prev, decisionDepartments: fixedDepartment }));
    }

  }, [fixedDepartment]);

  // Genel Autocomplete handler (departments, user vb.)
  const handleAutocompleteChange = (field) => (event, newValue) => {
    if (field === "decisionDepartments" && fixedDepartment && Array.isArray(fixedDepartment)) {
      // Eğer zorunlu departmanlar varsa, kullanıcı en az sabit (fixedDepartment) değerlerden bir tanesini silmemeli.
      const fixedSelected = fixedDepartment.filter(dep => newValue.includes(dep));
      // Eğer hiç fixed değerden seçili kalmadıysa, son sabiti tekrar ekle.
      if (fixedSelected.length === 0) {
        newValue = [...newValue, fixedDepartment[fixedDepartment.length - 1]];
      }
      setFilters((prev) => ({ ...prev, [field]: newValue }));
    } else {
      setFilters((prev) => ({ ...prev, [field]: newValue }));
    }
  };

  const handleAutocompleteChangeInst = (field) => (event, newValue) => {
    // Eğer type !== "yk" ve fixedInst değerleri tanımlıysa (ör: ["ATAM", "TDK"])
    if (type !== "yk" && fixedInst && fixedInst.length > 0) {
      // Yeni değerde, fixedInst içindeki kaç tane değer kaldı, ona bakalım.
      const fixedCount = newValue.filter(val => fixedInst.includes(val)).length;
      // Eğer fixedInst içerisinden hiç sabit değer kalmamışsa,
      // (yani kullanıcı tüm sabitleri silmeye çalıştıysa) 
      // sabit değerlerden sonuncusunu (veya tercih edilen başka birini) yeniden ekliyoruz.
      if (fixedCount === 0) {
        newValue.push(fixedInst[fixedInst.length - 1]);
      }
    }

    if (newValue.length === 0) {
      setFilters(prev => ({ ...prev, [field]: dataInstOptions }));
    } else {
      setFilters((prev) => ({ ...prev, [field]: newValue }));
    }

    // meetingInst değişirse, Units'i güncelle
    if (field === "meetingInst") {
      const filteredUnits = unitList.filter(unit => newValue.includes(unit.inst));
      setDataUnitOptions(filteredUnits);
    }
  };



  const handleCheckboxChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.checked ? 1 : 0 }));
  };



  const [expanded, setExpanded] = useState(false);

  const labelMapping = {
    principle: "İlke Kararı",
    secret: "Gizli",
    supp: "Ek Gündem"
  };


  const handleSearch = async () => {
    if (setSelectedFilters) {
      setSelectedFilters(filters);
    }
    setExpanded(false);
    try {
      setLoading(true);
      let statusFilter = null;
      if (applied && !notApplied) {
        statusFilter = 1;
      } else if (!applied && notApplied) {
        statusFilter = 0;
      }
      const searchParams = {
        decisionDepartments,
        decisionSubjectIds,
        agendaText,
        principle,
        supp,
        secret,
        status: statusFilter,
        meetingNo,
        meetingAdminUserIds,
        meetingInst,
        meetingUnitIds: (type === "yk" && !anotherYkUnit) ? [prodYKToplantisiUnitId] : meetingUnitIds,
        statFinish,
        pdateStart: pdateStart ? pdateStart.toISOString() : null,
        pdateEnd: pdateEnd ? pdateEnd.toISOString() : null,
        meetingCreatorUserIds,
        meetingParticipantUserIds,
        statusReviewUserIds,
        pageType: "dstatus"
      };
      const res = await getDecisionStatusListApi(searchParams);
      setFilterData(res.data);
      if (setSearchType) {
        setSearchType("filter");
      }
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      setSidebarOptions({ open: false });
      setLoading(false);
    }
  };

  const handleClear = () => {
    let defaultMeetingInst = [];

    if (type === "yk") {
      defaultMeetingInst = ["YK"];
    } else if (isRoleCheckList(authUser.userRoles, [1])) {
      defaultMeetingInst = dataInstOptions;
    } else {
      const currentInst = dataInstOptions.length > 0 ? dataInstOptions[0] : '';
      defaultMeetingInst = currentInst ? [currentInst] : [];
    }

    // Default department ayarla
    let defaultDepartments = [];
    if (fixedDepartment && Array.isArray(fixedDepartment) && fixedDepartment.length > 0) {
      defaultDepartments = fixedDepartment;
    }

    setFilters({
      pageType: "dstatus",
      decisionDepartments: type === "yk" ? defaultDepartments : [],
      decisionSubjectIds: [],
      agendaText: "",
      principle: null,
      supp: null,
      secret: null,
      applied: false,
      notApplied: false,
      meetingNo: "",
      meetingAdminUserIds: [],
      meetingInst: defaultMeetingInst,
      meetingUnitIds: [],
      statFinish: null,
      pdateStart: null,
      pdateEnd: null,
      meetingCreatorUserIds: [],
      meetingParticipantUserIds: [],
      statusReviewUserIds: [],
    });
    setFilterData([]);
    if (setSelectedFilters) {
      setSelectedFilters({});
    }
    if (setSearchType) {
      setSearchType(null);
    }
  };
  return (
    <Accordion
      expanded={expanded}
      onChange={(event, isExpanded) => setExpanded(isExpanded)}
      sx={{ background: "#e1e1e1" }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
        <Typography>Filtreler</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} padding={1} sx={{ background: "#e1e1e1" }}>
          {/* --------------------- */}
          {/* Satır 1: kurum, ilgili kurum, konu, birim ve meetingNo */}
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} sm={3} lg={2}>
              <Autocomplete
                multiple
                id="meetingInst"
                options={dataInstOptions}
                value={meetingInst}
                onChange={(event, newValue) => {
                  handleAutocompleteChangeInst("meetingInst")(event, newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Kurum" placeholder="Seçiniz" size="small" />
                )}

                disabled={type === "yk" || (meetingInst !== null && meetingInst.length === 1) ? true : false}
              />
            </Grid>
            <Grid item xs={12} sm={3} lg={2}>
              <Autocomplete
                multiple
                id="decisionDepartments"
                options={dataDepartmentOptions}
                value={decisionDepartments}
                onChange={handleAutocompleteChange("decisionDepartments")}
                renderInput={(params) => (
                  <TextField {...params} label="İlgili Kurum/Birim" placeholder="Seçiniz" size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3} lg={2}>
              <Autocomplete
                multiple
                id="decisionSubjectIds"
                options={dataSubjectOptions}
                getOptionLabel={(option) => option.name || ""}
                value={decisionSubjectIds}
                onChange={handleAutocompleteChange("decisionSubjectIds")}
                renderInput={(params) => (
                  <TextField {...params} label="Konu" placeholder="Seçiniz" size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3} lg={3}>
              <Autocomplete
                multiple
                id="meetingUnitIds"
                options={dataUnitOptions}
                getOptionLabel={(option) => option.name}
                value={(type === "yk" && !anotherYkUnit) ? [unitList.find(unit => unit.id === prodYKToplantisiUnitId)] : meetingUnitIds}
                onChange={(type === "yk" && !anotherYkUnit) ? undefined : handleAutocompleteChange("meetingUnitIds")}
                renderInput={(params) => (
                  <TextField {...params} label="Kurul/Kol/Komisyon" placeholder="Seçiniz" size="small" />
                )}
                disabled={(type === "yk" && !anotherYkUnit)}
              />
            </Grid>
            <Grid item xs={12} sm={3} lg={2}>
              <TextField
                id="meetingNo"
                label="Toplantı Sayı"
                value={meetingNo}
                onChange={handleInputChange("meetingNo")}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
          {/* --------------------- */}
          {/* Satır 4: üyeler, katılımcılar, uygulanma durumu güncelleyenler, toplantıyı oluşturan */}
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} sm={4} lg={3}>
              <Autocomplete
                multiple
                id="meetingCreatorUserIds"
                options={userListOptions}
                getOptionLabel={(option) => getUserFullName(option)}
                value={meetingCreatorUserIds}
                onChange={handleAutocompleteChange("meetingCreatorUserIds")}
                renderInput={(params) => (
                  <TextField {...params} label="Üyeler" placeholder="Seçiniz" size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
              <Autocomplete
                multiple
                id="meetingParticipantUserIds"
                options={userListOptions}
                getOptionLabel={(option) => getUserFullName(option)}
                value={meetingParticipantUserIds}
                onChange={handleAutocompleteChange("meetingParticipantUserIds")}
                renderInput={(params) => (
                  <TextField {...params} label="Katılımcılar" placeholder="Seçiniz" size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
              <Autocomplete
                multiple
                id="statusReviewUserIds"
                options={userListOptions}
                getOptionLabel={(option) => getUserFullName(option)}
                value={statusReviewUserIds}
                onChange={handleAutocompleteChange("statusReviewUserIds")}
                renderInput={(params) => (
                  <TextField {...params} label="Uygulanma Durumu Güncelleyenler" placeholder="Seçiniz" size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
              <Autocomplete
                multiple
                id="meetingAdminUserIds"
                options={userListOptions}
                getOptionLabel={(option) => getUserFullName(option)}
                value={meetingAdminUserIds}
                onChange={handleAutocompleteChange("meetingAdminUserIds")}
                renderInput={(params) => (
                  <TextField {...params} label="Toplantı Yöneticisi" placeholder="Seçiniz" size="small" />
                )}
              />
            </Grid>
          </Grid>
          {/* --------------------- */}
          {/* Satır 2: agenda ve karar text input alanları */}
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} sm={12} lg={12}>
              <TextField
                id="agendaText"
                label="Gündem"
                value={agendaText}
                onChange={handleInputChange("agendaText")}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
          {/* --------------------- */}
          {/* Satır 3: ilke kararı, ek gündem, gizli, tamamlanan toplantılar, uygulananlar, uygulanmayanlar checkbox'ları ve tarih picker'ları */}
          <Grid container item xs={12} spacing={2}>
            <Grid container item xs={4} >
              <Grid item xs={12} sm={12} lg={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                  <DatePicker
                    label="Başlangıç Tarihi"
                    value={pdateStart}
                    onChange={handleDateChange("pdateStart")}
                    renderInput={(params) => <TextField size="small" sx={{ margin: '5px' }} {...params} />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={12} lg={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                  <DatePicker
                    label="Bitiş Tarihi"
                    value={pdateEnd}
                    onChange={handleDateChange("pdateEnd")}
                    renderInput={(params) => <TextField size="small" sx={{ margin: '5px' }} {...params} />}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Grid container item xs={8} >
              <Grid item xs={12} sm={3} lg={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={principle === 1}
                      onChange={handleCheckboxChange("principle")}
                    />
                  }
                  label={labelMapping.principle}
                />
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={supp === 1}
                      onChange={handleCheckboxChange("supp")}
                    />
                  }
                  label={labelMapping.supp}
                />
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={secret === 1}
                      onChange={handleCheckboxChange("secret")}
                    />
                  }
                  label={labelMapping.secret}
                />
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={statFinish === 1}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, statFinish: e.target.checked ? 1 : 0 }))
                      }
                    />
                  }
                  label="Tamamlanan Toplantılar"
                />
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={applied}
                      disabled={notApplied}
                      onChange={(e) => setFilters((prev) => ({ ...prev, applied: e.target.checked }))}
                    />
                  }
                  label="Uygulananlar"
                />
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notApplied}
                      disabled={applied}
                      onChange={(e) => setFilters((prev) => ({ ...prev, notApplied: e.target.checked }))}
                    />
                  }
                  label="Uygulanmayanlar"
                />
              </Grid>
            </Grid>
          </Grid>
          {/* --------------------- */}
          {/* Ara ve Temizle Butonları */}
          <Grid item xs={12} container justifyContent="flex-end" spacing={1}>
            <Grid item>
              <Button variant="contained" size="small" onClick={handleSearch}>
                Ara
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" size="small" onClick={handleClear}>
                Temizle
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

export default DecisionStatusListDataTableFilter;
